/**
 * デモデータ専用管理サービス
 * デモ体験用データの管理とリセット機能を提供
 */

import { isDemoMode } from '../utils/environment';
import { AppError, ErrorCategory, ErrorLevel } from '../utils/errorHandler';

export interface DemoDataStatus {
  isDemo: boolean;
  lastResetAt?: Date;
  scenarioVersion: string;
  dataIntegrity: boolean;
}

export interface DemoScenario {
  id: string;
  name: string;
  description: string;
  estimatedDuration: number; // minutes
  steps: DemoStep[];
}

export interface DemoStep {
  id: string;
  title: string;
  description: string;
  action: string;
  expectedResult: string;
  order: number;
}

/**
 * デモデータ管理サービスクラス
 */
export class DemoDataService {
  private static instance: DemoDataService;
  private readonly SCENARIO_VERSION = '1.0.0';
  private readonly STORAGE_KEY = 'demo_data_status';

  private constructor() {
    // シングルトンパターン
  }

  public static getInstance(): DemoDataService {
    if (!DemoDataService.instance) {
      DemoDataService.instance = new DemoDataService();
    }
    return DemoDataService.instance;
  }

  /**
   * デモモード状態の確認
   */
  public getDemoStatus(): DemoDataStatus {
    const isDemo = isDemoMode();
    const storedStatus = this.getStoredStatus();

    return {
      isDemo,
      lastResetAt: storedStatus?.lastResetAt,
      scenarioVersion: this.SCENARIO_VERSION,
      dataIntegrity: this.checkDataIntegrity(),
    };
  }

  /**
   * デモシナリオ一覧の取得
   */
  public getAvailableScenarios(): DemoScenario[] {
    return [
      {
        id: 'basic-flow',
        name: '基本配車フロー体験',
        description: '案件登録→配車→進捗管理の基本的な操作を体験できます',
        estimatedDuration: 15,
        steps: [
          {
            id: 'step1',
            title: '案件登録',
            description: '新しい陸送案件を登録します',
            action: '案件管理 > 新規案件作成',
            expectedResult: '案件が一覧に表示される',
            order: 1,
          },
          {
            id: 'step2',
            title: '配車実行',
            description: '登録した案件に陸送員を配車します',
            action: '配車管理 > 自動配車または手動配車',
            expectedResult: '案件のステータスが「配車済み」に変更',
            order: 2,
          },
          {
            id: 'step3',
            title: '進捗確認',
            description: '配車された案件の進捗を確認します',
            action: 'ダッシュボード > 進行中案件確認',
            expectedResult: '配車状況と進捗が表示される',
            order: 3,
          },
        ],
      },
      {
        id: 'master-management',
        name: 'マスターデータ管理体験',
        description: '顧客・陸送員・車両などのマスターデータ管理を体験できます',
        estimatedDuration: 10,
        steps: [
          {
            id: 'step1',
            title: '顧客データ登録',
            description: '新規顧客情報を登録します',
            action: '顧客管理 > 新規顧客作成',
            expectedResult: '顧客が一覧に表示される',
            order: 1,
          },
          {
            id: 'step2',
            title: '陸送員データ管理',
            description: '陸送員情報の確認・更新を行います',
            action: '陸送員管理 > データ編集',
            expectedResult: '陸送員情報が更新される',
            order: 2,
          },
        ],
      },
      {
        id: 'reporting',
        name: 'レポート機能体験',
        description: '各種レポートの閲覧・出力機能を体験できます',
        estimatedDuration: 8,
        steps: [
          {
            id: 'step1',
            title: 'ダッシュボード確認',
            description: '配車状況の統計情報を確認します',
            action: 'ダッシュボード > 統計情報確認',
            expectedResult: '各種統計グラフが表示される',
            order: 1,
          },
          {
            id: 'step2',
            title: 'CSV出力',
            description: '案件データをCSV形式で出力します',
            action: '案件管理 > CSV出力',
            expectedResult: 'CSVファイルがダウンロードされる',
            order: 2,
          },
        ],
      },
    ];
  }

  /**
   * デモデータリセット実行後の状態更新
   */
  public markDataReset(): void {
    if (!isDemoMode()) {
      throw new AppError(
        'デモモードでのみ実行可能です',
        ErrorCategory.AUTHORIZATION,
        ErrorLevel.HIGH
      );
    }

    const status: DemoDataStatus = {
      isDemo: true,
      lastResetAt: new Date(),
      scenarioVersion: this.SCENARIO_VERSION,
      dataIntegrity: true,
    };

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(status));
    console.log('✅ デモデータリセット状態を記録しました');
  }

  /**
   * デモガイダンス表示が必要かの判定
   */
  public shouldShowGuidance(): boolean {
    // ウェルカムダッシュボードを一時的に非表示にする
    // 実装は残しておき、将来的に再度有効化可能
    return false;

    // 以下、元の実装（将来の再有効化用）
    /*
    if (!isDemoMode()) {
      return false;
    }

    const storedStatus = this.getStoredStatus();
    const hasBeenReset = storedStatus?.lastResetAt;
    const isFirstVisit = !hasBeenReset;

    return isFirstVisit;
    */
  }

  /**
   * デモデータの整合性チェック
   */
  private checkDataIntegrity(): boolean {
    if (!isDemoMode()) {
      return true; // 本番モードでは常にtrue
    }

    // 基本的な整合性チェック（簡易版）
    // 実際の実装では、必要なデモデータが存在するかを確認
    try {
      const hasLocalStorage = typeof Storage !== 'undefined';
      return hasLocalStorage;
    } catch (error) {
      console.warn('デモデータ整合性チェックエラー:', error);
      return false;
    }
  }

  /**
   * 保存された状態の取得
   */
  private getStoredStatus(): DemoDataStatus | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          ...parsed,
          lastResetAt: parsed.lastResetAt ? new Date(parsed.lastResetAt) : undefined,
        };
      }
    } catch (error) {
      console.warn('保存されたデモ状態の取得エラー:', error);
    }
    return null;
  }

  /**
   * デモデータクリーンアップ
   */
  public cleanup(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      console.log('デモデータ状態をクリーンアップしました');
    } catch (error) {
      console.warn('デモデータクリーンアップエラー:', error);
    }
  }
}

// デフォルトエクスポート（シングルトンインスタンス）
export const demoDataService = DemoDataService.getInstance();
